// src/App.js

import React, { useContext } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Login from './components/Authentication/Login';
import MainScreen from './components/Main/MainScreen';
import PasswordRecovery from './components/Authentication/PasswordRecovery';
import ProtectedRoute from './components/Authentication/ProtectedRoute';
import ResetPassword from './components/Authentication/ResetPassword';
import PrivacyPolicy from './components/Policies/PrivacyPolicy';
import TermsService from './components/Policies/TermsService';
import ColorSettings from './components/Common/ColorSettings';
import LanguageSettings from './components/Common/LanguageSettings';
import { DataProvider, DataContext } from './DataContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ErrorBoundary from './components/Common/ErrorBoundary';
import './App.css';
import { useColorSettings } from './components/Common/ColorSettingsContext';
import { useLanguageSettings } from './components/Common/LanguageSettingsContext';
import Layout from './components/Common/Layout'; // Import the Layout component

const queryClient = new QueryClient();

const AppContent = () => {
  const { loading: colorLoading, error: colorError } = useColorSettings();
  const { loading: languageLoading, error: languageError } = useLanguageSettings();
  const { allRecordsLoading, allRecordsError } = useContext(DataContext);

  // Handle errors
  if (colorError || languageError || allRecordsError) {
    return (
      <div className="error-container">
        {colorError && <p>{colorError.message || 'Error loading color settings.'}</p>}
        {languageError && <p>{languageError.message || 'Error loading language settings.'}</p>}
        {allRecordsError && <p>{allRecordsError.message || 'Error loading records.'}</p>}
        {/* Optionally, include retry mechanisms or more detailed error handling */}
      </div>
    );
  }


  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route
          path="/main"
          element={
            <ProtectedRoute>
              <MainScreen />
            </ProtectedRoute>
          }
        />
        <Route path="/password-recovery" element={<PasswordRecovery />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsService />} />
      </Routes>
    </Layout>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Optional: Include React Query Devtools for debugging */}
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}

      {/* Error Boundary for better error handling */}
      <ErrorBoundary>
        {/* DataProvider to provide data context */}
        <DataProvider>
          {/* LanguageSettings to manage language context */}
          <LanguageSettings>
            {/* ColorSettings to manage theming */}
            <ColorSettings>
              <Router>
                {/* Render AppContent which handles loading and routing */}
                <AppContent />
              </Router>
            </ColorSettings>
          </LanguageSettings>
        </DataProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
};

export default App;
